import useAuthModal from "@/hooks/useAuthModal";
import { Input } from "@jammable/ui-components/Forms";
import axios from "axios";
import { getAuth } from "firebase/auth";
import { useFormik } from "formik";
import Image from "next/image";
import React, { useState } from "react";
import toast from "react-hot-toast";
import { useInfiniteQuery, useQueryClient } from "react-query";
import * as yup from "yup";
import { Heart } from "react-iconly";
import ModelCommentItem from "./ModelCommentItem";
import { Spinner } from "@jammable/ui-components/Loader";
import { Button, SecondaryButton } from "@jammable/ui-core";
import { Badge } from "@jammable/ui-components/Badge";
import NotFound from "../../../apps/web/components/NotFound";
import Dropdown from "./dropdown/Dropdown";
import { DropdownMenuItem } from "@jammable/ui-components/DropdownMenu";
import { capitalizeFirstLetter } from "@/utils/tools";
import { ChevronDown } from "lucide-react";

const ModelCommentSection = ({ user, model }) => {
  const [commenting, setCommenting] = useState(false);
  const { checkAuthAndExecute } = useAuthModal();
  const queryClient = useQueryClient();
  const [loading, setLoading] = useState(false);

  const [sort, setSort] = useState("top");

  const validationSchema = yup.object().shape({
    comment: yup
      .string()
      .required("Text is required")
      .max(500, "Maximum 500 characters allowed!"),
  });
  const formik = useFormik({
    initialValues: {
      comment: "",
    },
    validationSchema,
    onSubmit: async (values) => {
      if (!checkAuthAndExecute()) return false;
      setCommenting(true);

      const auth = getAuth();
      const token = await auth.currentUser.getIdToken();

      axios
        .post(
          `/api/comment/createComment`,
          {
            body: values.comment,
            modelId: model?._id,
          },
          {
            headers: {
              Authorization: token,
            },
          },
        )
        .then(() => {
          queryClient.invalidateQueries(["comments", model?._id]).then(() => {
            toast.success("Comment posted!");
            setCommenting(false);
            formik.setFieldValue("comment", "");
          });
        })
        .catch((err) => {
          setCommenting(false);
          toast.error(err?.response?.data?.message);
        });
    },
  });
  const limit = 5;
  const fetchComments = async ({ pageParam = 0 }) => {
    const auth = getAuth();
    const token = await auth?.currentUser?.getIdToken();
    const res = await axios.get(
      `/api/comment/getCommentsModel?page=${pageParam}`,
      {
        params: {
          modelId: model?._id,
          limit: limit,
          token: token,
          sort: sort,
        },
        headers: {
          Authorization: token,
        },
      },
    );
    const data = res.data;
    return data;
  };

  const commentData = useInfiniteQuery(
    ["comments", model?._id, sort],
    fetchComments,
    {
      getNextPageParam: (lastpage, allpages) => {
        const maxPages = Math.ceil(lastpage?.length / limit);
        const nextPage = allpages.length + 1;
        return nextPage <= maxPages ? nextPage : null;
      },
      refetchOnWindowFocus: true,
      onError: (error) => {},
    },
  );

  const sortOptions = [
    { value: "top", label: "Top" },

    { value: "newest", label: "Newest" },
  ];

  return (
    <div className=" tw-flex tw-mt-14 tw-w-full tw-gap-3 md:tw-gap-0  tw-flex-col md:tw-flex-row">
      <div className="tw-font-bold tw-text-xl tw-items-start tw-text-left tw-flex-row md:tw-flex-col tw-justify-start tw-w-full tw-flex md:tw-w-1/3  tw-gap-1 tw-h-max">
        {model?.comments} Comments
        <Dropdown
          dropdownItems={
            <>
              {sortOptions.map((option) => (
                <DropdownMenuItem
                  key={option.value}
                  onClick={() => {
                    setSort(option.value);
                  }}
                  className={`tw-flex tw-transition-all tw-text-sm hover:tw-bg-gray-100 tw-cursor-pointer tw-items-center tw-font-bold tw-gap-2 tw-p-2 tw-rounded-3xl ${
                    sort === option.value
                      ? "tw-bg-purple-200 tw-text-purple-700 hover:tw-bg-purple-200"
                      : ""
                  }`}
                >
                  {option.label}
                </DropdownMenuItem>
              ))}
            </>
          }
        >
          <div className="tw-w-max tw-text-sm tw-cursor-pointer hover:tw-bg-gray-50 tw-transition-all tw-items-center tw-relative tw-text-left tw-align-left tw-flex tw-justify-between tw-gap-1 tw-border-2 tw-border-black/10 tw-border-solid tw-p-1 tw-px-2 tw-rounded-xl tw-font-black">
            {capitalizeFirstLetter(sort) || "Sort by"}
            <ChevronDown strokeWidth={3} size={16} />
          </div>
        </Dropdown>
      </div>

      <div className="tw-w-full md:tw-w-2/3">
        <form
          onSubmit={formik.handleSubmit}
          className="tw-flex tw-items-center tw-gap-3"
        >
          <Input
            name="comment"
            className="tw-w-full tw-h-12 tw-rounded-full"
            value={formik.values.comment}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={formik.touched.comment && formik.errors.comment}
            placeholder="Add comment..."
          />
          <Button
            disabled={commenting}
            className=" w-flex tw-items-center tw-h-12 md:tw-px-6 tw-rounded-full"
            type="submit"
          >
            {commenting ? (
              <Spinner className="tw-border-white" size={4} />
            ) : (
              "Post"
            )}
          </Button>
        </form>

        <div className="tw-flex tw-flex-col tw-mt-6 tw-gap-4">
          {commentData?.data?.pages[0]?.comments?.length === 0 && (
            // <div className="tw-font-bold tw-text-center tw-mt-2">
            //   No comments here...
            // </div>
            <NotFound
              title="No comments here..."
              description="Start the conversation!"
              animateIn={false}
            />
          )}

          {commentData?.data?.pages?.map((page) =>
            page?.comments?.map((comment) => {
              return comment?.parentid === null ? (
                <ModelCommentItem
                  key={comment?._id}
                  comment={comment}
                  model={model}
                  page={page}
                />
              ) : null;
            }),
          )}
          {commentData?.isLoading && (
            <div className="tw-w-full tw-flex tw-justify-center">
              <Spinner size={5} className="tw-border-3" />
            </div>
          )}
          <div className="tw-w-full tw-flex tw-justify-center">
            {commentData?.hasNextPage && (
              <SecondaryButton
                onClick={() => {
                  commentData.fetchNextPage();
                }}
                style={{
                  fontSize: 14,
                  padding: 14,
                  paddingTop: 8,
                  paddingBottom: 8,
                  height: 40,
                  display: "flex",
                }}
                className="tw-w-max tw-px-2  tw-text-black tw-border-2 tw-rounded-full"
              >
                {commentData?.isFetchingNextPage ? (
                  <Spinner size={5} className="tw-border-3 tw-border-black" />
                ) : (
                  "View More Comments"
                )}
              </SecondaryButton>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ModelCommentSection;
